/**
 * 大厅的流程状态机：首页 ⇄ 建房 ⇄ 加入，以及「发出去之后发生了什么」。
 *
 * 为什么需要一个 store 而不是组件内 useState：`Lobby`（stage 区）与 `LobbyDock`
 * （dock 区）是 App 的两个**平行 slot**，DOM 上不是父子关系，没法用 props 共享
 * 「现在在哪一屏 / 正在提交 / 出了什么错」。
 *
 * ★ 建房与加入都走 **WS**（`createRoom` / `joinRoom`），不是 HTTP：
 *   服务端的 `ROOM_ENTRY` 分支要求这条连接在进房前没有绑定房间，且成功的结果
 *   就是这条连接上到来的第一份 `roomSnapshot`。走 HTTP 建房再靠 WS 进房会多一次
 *   往返，还会在「HTTP 成功但 WS 没连上」时留下一个没人进去的空房。
 *
 * ★ 成功/失败的判据：
 *   - 成功 = `useNetStore.view` 从 null 变成有房间码的快照；
 *   - 失败 = `useNetStore.lastError` 换了一个新对象。
 *   协议的 `error` 帧带 `refCid`，但 `applyMessage` 的 `NetError` 没有保留它
 *   （见 deviations）。大厅这一屏除了我们自己没有别的消息在飞，因此
 *   「pending 期间来的第一条 error」就是我们这条的回复，够用且不会误伤牌桌。
 */
import { create } from 'zustand'
import { deckVariantOf, type ClientMessage, type DeckVariant, type ErrorCode, type PlayerView } from '@poker/shared'

import { useNetStore, type NetError, type NetFatal } from '../transport/applyMessage.js'
import type { ConnectionPhase } from '../store/tableStore.js'
import { normalizeRoomCode, roomCodeProblem } from './roomCode.js'
import {
  DEFAULT_CREATE_FORM,
  toCreateRoomConfig,
  validateCreateForm,
  type CreateRoomForm,
} from './createForm.js'
import { nicknameProblem, sanitizeNickname, truncateNickname } from './useAuth.js'

export type LobbyScreen = 'home' | 'create' | 'join'

/**
 * 大厅只需要传输层的一个很窄的切面。`WsTransport` 天然满足它，
 * 测试里塞一个 `{ send: () => 1 }` 就够。
 */
export interface LobbyPort {
  send(msg: ClientMessage): number
  phase?(): ConnectionPhase
  /** 大厅按「重试」时可以催一下重连 */
  retryNow?(): void
}

let port: LobbyPort | null = null

/** 接线层建好 transport 之后调一次。传 null 解绑（测试 / 登出）。 */
export function setLobbyPort(next: LobbyPort | null): void {
  port = next
}

export function getLobbyPort(): LobbyPort | null {
  return port
}

/**
 * 发出去之后多久还没有任何回应就认输。
 * 取 15s 是因为 `send` 在连接没建好时会进 outbox、等 `welcome` 之后补发——
 * 弱网下「连上」本身就可能要十秒，太短会把还在路上的请求判死。
 */
export const LOBBY_TIMEOUT_MS = 15_000

export interface LobbyPending {
  readonly kind: 'create' | 'join'
  readonly cid: number
  readonly at: number
}

export interface CreatedRoom {
  readonly code: string
  readonly roomId: string
  /** 是不是这台设备刚刚建的（房主要先看码，不能被自动带进牌桌） */
  readonly justCreated: boolean
  /** 服务端实际开出来的玩法（读快照，不读表单）；缺省 = 标准 */
  readonly deckVariant?: DeckVariant
}

export interface LobbyFlowState {
  readonly screen: LobbyScreen
  readonly pending: LobbyPending | null
  /** 面向用户的一句话，已经翻成「房间不存在 / 房间满了」这种具体说法 */
  readonly error: string | null
  readonly errorCode: ErrorCode | null
  readonly room: CreatedRoom | null
  /** true = 已经在房间里且可以去牌桌了 */
  readonly entered: boolean
  /** 上一次输入过的房间码，返回上一屏再进来不用重打 */
  readonly lastCode: string
  /** 建房表单。和 lastCode 同理：stage 上改、dock 上提交，必须共享一份 */
  readonly form: CreateRoomForm

  go(screen: LobbyScreen): void
  setLastCode(code: string): void
  setForm(next: CreateRoomForm): void
  submitCreate(form: CreateRoomForm, nickname: string): boolean
  submitJoin(rawCode: string, nickname: string): boolean
  /** 房主看完房间码，主动进牌桌 */
  enterTable(): void
  noteSnapshot(view: PlayerView): void
  noteError(err: NetError): void
  noteFatal(fatal: NetFatal): void
  /** 连接上没有房间了（离开 / 被关房）：回到首页 */
  noteLeft(): void
  noteTimeout(): void
  fail(message: string, code?: ErrorCode | null): void
  clearError(): void
  reset(): void
}

const INITIAL = {
  screen: 'home' as LobbyScreen,
  pending: null as LobbyPending | null,
  error: null as string | null,
  errorCode: null as ErrorCode | null,
  room: null as CreatedRoom | null,
  entered: false,
  lastCode: '',
  form: DEFAULT_CREATE_FORM,
}

/** 超时定时器。放模块级而不是 state：它不参与渲染，进 state 只会多一次重渲染。 */
let timeoutHandle: ReturnType<typeof setTimeout> | null = null

function clearTimeoutHandle(): void {
  if (timeoutHandle !== null) {
    clearTimeout(timeoutHandle)
    timeoutHandle = null
  }
}

function armTimeout(): void {
  clearTimeoutHandle()
  const handle = setTimeout(() => {
    timeoutHandle = null
    useLobbyFlow.getState().noteTimeout()
  }, LOBBY_TIMEOUT_MS)
  // Node 侧（测试）别因为这个定时器吊住进程；浏览器里 setTimeout 返回 number，没有 unref
  ;(handle as unknown as { unref?: () => void }).unref?.()
  timeoutHandle = handle
}

/**
 * 错误码 → 具体说法。**绝不出现「加入失败」这种笼统文案**：
 * 用户手里只有一串码，他要知道的是「码错了」还是「房满了」还是「这局结束了」。
 */
export function describeRoomError(
  code: ErrorCode | null,
  serverMessage: string,
  kind: 'create' | 'join',
): string {
  switch (code) {
    case 'E_ROOM_NOT_FOUND':
      return '没有这个房间，再对一遍房间码'
    case 'E_ROOM_CODE_INVALID':
      return '房间码不对，再对一遍'
    case 'E_ROOM_FULL':
      return '房间满了，等有人离开'
    case 'E_ROOM_CLOSED':
      return '这个房间已经结束了'
    case 'E_BANNED':
      return '房主把你请出了这个房间'
    case 'E_ALREADY_IN_ROOM':
      return '你已经在另一个房间里，先退出'
    case 'E_ROOM_LIMIT':
      return kind === 'create' ? '你同时开的房间太多了，先关掉一个' : '这个房间暂时不能加入，稍后再试'
    case 'E_RATE_LIMITED':
      return '太快了，等几秒再试'
    case 'E_NOT_AUTHENTICATED':
    case 'E_AUTH_EXPIRED':
    case 'E_AUTH_INVALID':
      return '身份过期了，正在重新登录，稍后再试'
    case 'E_PROTOCOL_VERSION':
      return '客户端版本太旧了，刷新一下页面'
    case 'E_CLIENT_OUTDATED':
      // 目前只有短牌房会回这个码（旧版页面不认识短牌）；新版页面上不会出现
      return '这个房间需要新版本，关掉所有页面后重新打开'
    case 'E_INTERNAL':
      return '服务器出了点问题，再试一次'
    default:
      // 兜底也要带上服务端那句中文，比「未知错误」有用得多
      return serverMessage.trim() === ''
        ? kind === 'create'
          ? '创建没成功，再试一次'
          : '没能加入，再试一次'
        : serverMessage
  }
}

export const useLobbyFlow = create<LobbyFlowState>((set, get) => ({
  ...INITIAL,

  go: (screen) => set({ screen, error: null, errorCode: null }),

  setLastCode: (code) => set({ lastCode: code }),

  setForm: (next) => set({ form: next, error: null, errorCode: null }),

  submitCreate(form, nickname) {
    const s = get()
    if (s.pending !== null) return false
    const errors = validateCreateForm(form)
    const first = errors[0]
    if (first !== undefined) {
      set({ error: first, errorCode: null })
      return false
    }
    const name = truncateNickname(sanitizeNickname(nickname))
    const nameProblem = nicknameProblem(name)
    if (nameProblem !== null) {
      set({ error: nameProblem, errorCode: null })
      return false
    }
    if (port === null) {
      set({ error: '连不上服务器', errorCode: null })
      return false
    }
    const cid = port.send({ type: 'createRoom', config: toCreateRoomConfig(form, name), nickname: name })
    set({ pending: { kind: 'create', cid, at: Date.now() }, error: null, errorCode: null })
    armTimeout()
    return true
  },

  submitJoin(rawCode, nickname) {
    const s = get()
    if (s.pending !== null) return false
    const code = normalizeRoomCode(rawCode)
    if (code === null) {
      // roomCodeProblem 会说清是「还差 2 位」还是「没有字母 O」
      set({ error: roomCodeProblem(rawCode) ?? '房间码不对', errorCode: null })
      return false
    }
    const name = truncateNickname(sanitizeNickname(nickname))
    const nameProblem = nicknameProblem(name)
    if (nameProblem !== null) {
      set({ error: nameProblem, errorCode: null })
      return false
    }
    if (port === null) {
      set({ error: '连不上服务器', errorCode: null })
      return false
    }
    const cid = port.send({ type: 'joinRoom', code, nickname: name })
    set({ pending: { kind: 'join', cid, at: Date.now() }, error: null, errorCode: null, lastCode: code })
    armTimeout()
    return true
  },

  enterTable: () =>
    set((s) => ({ entered: true, room: s.room === null ? null : { ...s.room, justCreated: false } })),

  noteSnapshot(view) {
    const s = get()
    const code = view.room.code
    const roomId = view.room.id
    if (s.room !== null && s.room.code === code && s.pending === null) return // 幂等：牌局中每秒好几帧
    clearTimeoutHandle()
    const justCreated = s.pending?.kind === 'create'
    set({
      pending: null,
      error: null,
      errorCode: null,
      room: { code, roomId, justCreated, deckVariant: deckVariantOf(view.room.config) },
      // 建房的人要先把码念给朋友，别自动跳进牌桌；加入的人当然是直接进去
      entered: !justCreated,
      lastCode: code,
    })
  },

  noteError(err) {
    if (get().pending === null) return
    clearTimeoutHandle()
    const kind = get().pending?.kind ?? 'join'
    set({
      pending: null,
      error: describeRoomError(err.code, err.message, kind),
      errorCode: err.code,
    })
  },

  noteFatal(fatal) {
    clearTimeoutHandle()
    set({ pending: null, error: fatal.message, errorCode: null, room: null, entered: false })
  },

  noteLeft() {
    if (get().room === null && !get().entered) return
    clearTimeoutHandle()
    set({ room: null, entered: false, pending: null, screen: 'home' })
  },

  noteTimeout() {
    if (get().pending === null) return
    set({
      pending: null,
      error: '没收到服务器回应，再试一次',
      errorCode: null,
    })
  },

  fail: (message, code = null) => set({ pending: null, error: message, errorCode: code }),

  clearError: () => set({ error: null, errorCode: null }),

  reset() {
    clearTimeoutHandle()
    set({ ...INITIAL })
  },
}))

/**
 * 把 `useNetStore` 的结果接到流程状态机上。大厅挂载时调一次，返回退订函数。
 *
 * 只读不写：所有判断都落在 `noteXxx` 上，测试可以直接调它们，不必造一整个 net 状态。
 */
export function startLobbyWatch(): () => void {
  let lastError: NetError | null = useNetStore.getState().lastError
  let lastFatal: NetFatal | null = useNetStore.getState().fatal

  // 挂载时先对齐一次当前状态：
  // ★ view 为 null 也要处理——上一局结束后回到大厅时 `entered` 还是 true，
  //   不清掉的话大厅会立刻把人弹回牌桌，形成来回跳。
  const initial = useNetStore.getState()
  if (initial.view !== null) useLobbyFlow.getState().noteSnapshot(initial.view)
  else useLobbyFlow.getState().noteLeft()

  return useNetStore.subscribe((s) => {
    const flow = useLobbyFlow.getState()
    if (s.lastError !== lastError) {
      lastError = s.lastError
      if (s.lastError !== null) flow.noteError(s.lastError)
    }
    if (s.fatal !== lastFatal) {
      lastFatal = s.fatal
      if (s.fatal !== null) flow.noteFatal(s.fatal)
    }
    if (s.view !== null) flow.noteSnapshot(s.view)
    else flow.noteLeft()
  })
}
